// Right rail — the active-context pane.
//
// v6 hosts heavyweight widgets here (form-invest, kyc-upload) that
// need to survive chat re-renders, plus an at-a-glance summary of
// the current contract / wallet / Safe / subscription state.
//
// §5.6.2 ships a minimal placeholder. §5.6.3 fills it with the
// active subscription's state when an investment cell is selected;
// Phase 6 wires the agent task pane.

import type { EnrichedSubscription } from '../../lib/investor';

type Props = {
  contractId?: string;
  investment?: EnrichedSubscription | null;
};

export function RightRail({ contractId, investment }: Props) {
  if (investment) {
    return <InvestmentContext sub={investment} />;
  }
  if (contractId) {
    return <OfferingContext contractId={contractId} />;
  }
  return <Empty />;
}

function InvestmentContext({ sub }: { sub: EnrichedSubscription }) {
  return (
    <div className="space-y-4 text-sm">
      <Header label="Active investment" />
      <Field label="Subscription">
        <code className="text-[11px] break-all">{sub.subscriptionId}</code>
      </Field>
      <Field label="Phase">
        <span>{sub.state?.currentPhase ?? '—'}</span>
      </Field>
      <Field label="Last event">
        <span>{sub.state?.lastEvent?.eventType ?? '—'}</span>
      </Field>
      <Field label="Restaurant">
        <span>{sub.subject?.rmintId ?? '—'}</span>
      </Field>
    </div>
  );
}

function OfferingContext({ contractId }: { contractId: string }) {
  return (
    <div className="space-y-4 text-sm">
      <Header label="Browsing offering" />
      <Field label="Restaurant">
        <span>{contractId}</span>
      </Field>
      <p className="text-xs text-foreground-mute border-t border-border pt-3">
        Live offering stats land in §5.6.3 alongside the cell's
        <code className="mx-1">$offering.*</code> bindings.
      </p>
    </div>
  );
}

function Empty() {
  return (
    <div className="text-sm text-foreground-mute space-y-3">
      <Header label="Context" />
      <p>Select a cell from the left rail.</p>
      <p className="text-xs">
        Phase 6 wires the agent task pane here — in-flight widgets
        (kyc upload, payment, signing) will live in this column so
        they survive cell navigation.
      </p>
    </div>
  );
}

function Header({ label }: { label: string }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-mute">
      {label}
    </h3>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-foreground-mute mb-0.5">
        {label}
      </p>
      <div className="text-foreground">{children}</div>
    </div>
  );
}
